'use client';

import { useEffect, useRef } from 'react';
import { useConvexAuth } from 'convex/react';

import { api, useMutation, useQuery } from '@/lib/convex';
import {
  clearPendingSignupLocale,
  getPendingAuthLocaleCookie,
} from '@/lib/auth-locale-preference';
import { shouldSyncPendingLocale } from '@/i18n/locale-preference';

export function AuthLocalePreferenceSync() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { data: profile } = useQuery(
    api.users.queries.getProfile,
    isAuthenticated ? {} : 'skip'
  );
  const updateRegionalSettings = useMutation(
    api.users.mutations.updateRegionalSettings
  );
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !profile || syncInFlightRef.current) {
      return;
    }

    const pendingLocale = getPendingAuthLocaleCookie();
    if (!pendingLocale) {
      return;
    }

    if (!shouldSyncPendingLocale(pendingLocale, profile.settings.language)) {
      clearPendingSignupLocale(pendingLocale);
      return;
    }

    syncInFlightRef.current = true;
    void updateRegionalSettings({
      language: pendingLocale,
      timezone:
        profile.settings.timezone?.trim() ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC',
    })
      .then(() => {
        clearPendingSignupLocale(pendingLocale);
      })
      .catch((error: unknown) => {
        syncInFlightRef.current = false;
        console.error('[auth-locale-sync] failed to update locale', error);
      });
  }, [isAuthenticated, isLoading, profile, updateRegionalSettings]);

  return null;
}
